// src/pages/AboutPage.jsx
import React from 'react';
import { useTheme } from '../context/ThemeContext';
import aboutTeam from '../assets/images/staff.png';
import aboutOffice from '../assets/images/about/tech-guys.jpg';
import aboutGuy from '../assets/images/about/tech-guy.jpg';

const AboutPage = () => {
  const { isDark } = useTheme();

  const values = [
    {
      title: 'Integrity',
      description: 'Ethical and transparent operations that build trust',
      icon: '🤝'
    },
    {
      title: 'Innovation',
      description: 'Continuous improvement and cutting-edge solutions',
      icon: '💡'
    },
    {
      title: 'Client-Centricity',
      description: 'We prioritize client needs and satisfaction',
      icon: '⭐'
    },
    {
      title: 'Collaboration',
      description: 'Working together to achieve better results',
      icon: '🔄'
    },
    {
      title: 'Excellence',
      description: 'Commitment to high-quality delivery',
      icon: '🏆'
    }
  ];

  const whyChooseUs = [
    {
      title: 'Proven Expertise',
      description: 'Years of software development experience and successful projects',
      icon: '✓'
    },
    {
      title: 'Customized Solutions',
      description: 'Tailored solutions designed for your unique business needs',
      icon: '🎯'
    },
    {
      title: 'Comprehensive Support',
      description: 'From consultation through post-implementation monitoring',
      icon: '🔄'
    }
  ];

  return (
    <div className="pt-20">

      {/* Hero Section */}
      <section className={`relative py-16 sm:py-20 lg:py-24 overflow-hidden ${
        isDark ? 'bg-black' : 'bg-gray-50'
      }`}>
        
        <div className="absolute inset-0 z-0">
          <img 
            src={aboutOffice}
            alt="DOROTS team collaborating"
            className="w-full h-full object-cover object-center"
          />

          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/70 to-black/50" />
        </div>

        {/* Blue glow effects */}
        {isDark && (
          <>
            <div className="absolute top-[-200px] left-[-100px] w-[500px] h-[500px] bg-blue-700/15 rounded-full blur-3xl z-0" />
            <div className="absolute bottom-[-250px] right-[-150px] w-[600px] h-[600px] bg-blue-800/15 rounded-full blur-3xl z-0" />
          </>
        )}
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">

            {/* Badge */}
            <div className="mb-4">
              <span className={`text-xs sm:text-sm tracking-[0.2em] font-medium px-4 py-2 rounded-full inline-block backdrop-blur-sm ${
                isDark 
                  ? 'text-blue-400 bg-blue-700/20 border border-blue-600/30' 
                  : 'text-blue-700 bg-white border border-blue-300'
              }`}>
                ABOUT US
              </span>
            </div>
            
            {/* Heading */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 leading-tight text-white">
              Who{' '}
              <span className="bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600 bg-clip-text text-transparent">
                We Are
              </span>
            </h1>
            
            {/* Description */}
            <p className="text-lg sm:text-xl leading-relaxed text-gray-200">
              We transform software development and SaaS applications,
              delivering solutions for industries including construction,
              manufacturing, mining, oil and gas, and food.
            </p>

          </div>
        </div>
      </section>


      {/* Who We Are Section */}
      <section className={`py-16 sm:py-20 ${
        isDark ? 'bg-black' : 'bg-white'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

            <div>
              <h2 className={`text-3xl sm:text-4xl font-bold mb-6 ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}>
                We are{' '}
                <span className="bg-gradient-to-r from-blue-500 to-blue-700 bg-clip-text text-transparent">
                  DOROTS
                </span>
              </h2>

              <p className={`text-lg leading-relaxed mb-6 ${
                isDark ? 'text-gray-300' : 'text-gray-600'
              }`}>
                Where innovation meets functionality to create business solutions.
                Founded to transform software development and SaaS applications,
                we deliver solutions for industries including construction,
                manufacturing, mining, oil and gas, and food.
              </p>

              <p className={`text-lg leading-relaxed ${
                isDark ? 'text-gray-300' : 'text-gray-600'
              }`}>
                Our commitment to excellence and client success drives everything
                we do, ensuring sustainable growth and technological advancement
                for businesses worldwide.
              </p>
            </div>

            <div className="relative">
              <div className={`rounded-2xl overflow-hidden shadow-2xl ${
                isDark ? 'border border-white/10' : 'border border-gray-200'
              }`}>
                <img 
                  src={aboutTeam} 
                  alt="DOROTS Team" 
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>

          </div>
        </div>
      </section>


      {/* Mission Section */}
      <section className={`py-16 sm:py-20 ${
        isDark ? 'bg-gray-900/30' : 'bg-blue-50'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

            <div className="order-2 lg:order-1">
              <div className={`rounded-2xl overflow-hidden shadow-2xl ${
                isDark ? 'border border-white/10' : 'border border-gray-200'
              }`}>
                <img 
                  src={aboutGuy} 
                  alt="DOROTS Office" 
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <h2 className={`text-3xl sm:text-4xl font-bold mb-6 ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}>
                Our{' '}
                <span className="bg-gradient-to-r from-blue-500 to-blue-700 bg-clip-text text-transparent">
                  Mission
                </span>
              </h2>

              <p className={`text-lg leading-relaxed mb-6 ${
                isDark ? 'text-gray-300' : 'text-gray-600'
              }`}>
                To empower businesses through innovative software solutions
                that streamline operations, enhance productivity, and foster growth.
              </p>

              <p className={`text-lg leading-relaxed ${
                isDark ? 'text-gray-300' : 'text-gray-600'
              }`}>
                We turn challenges into opportunities using robust technology,
                helping our clients remain competitive in an ever-evolving
                digital landscape.
              </p>
            </div>

          </div>
        </div>
      </section>


      {/* What We Do & Approach Section */}
      <section className={`py-16 sm:py-20 ${
        isDark ? 'bg-black' : 'bg-white'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

            {/* What We Do */}
            <div className={`p-8 rounded-2xl ${
              isDark 
                ? 'bg-white/5 border border-white/10' 
                : 'bg-gray-50 border border-gray-200'
            }`}>
              <div className="text-5xl mb-4">⚙️</div>

              <h3 className={`text-2xl font-bold mb-4 ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}>
                What We Do
              </h3>

              <p className={`leading-relaxed ${
                isDark ? 'text-gray-300' : 'text-gray-600'
              }`}>
                We optimize productivity while focusing on quality and process
                improvement. We design and develop sustainable solutions built
                on technology and expertise, ensuring your business stays ahead
                of the curve.
              </p>
            </div>


            {/* Our Approach */}
            <div className={`p-8 rounded-2xl ${
              isDark 
                ? 'bg-white/5 border border-white/10' 
                : 'bg-gray-50 border border-gray-200'
            }`}>
              <div className="text-5xl mb-4">🔄</div>

              <h3 className={`text-2xl font-bold mb-4 ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}>
                Our Approach
              </h3>

              <p className={`leading-relaxed ${
                isDark ? 'text-gray-300' : 'text-gray-600'
              }`}>
                We use a collaborative methodology involving clients at every
                step, ensuring customized, innovative, and adaptable solutions
                that perfectly align with your business goals and operational
                requirements.
              </p>
            </div>

          </div>
        </div>
      </section>


      {/* Values Section */}
      <section className={`py-16 sm:py-20 ${
        isDark ? 'bg-gray-900/30' : 'bg-blue-50'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="text-center mb-12">
            <h2 className={`text-3xl sm:text-4xl font-bold mb-4 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              Our{' '}
              <span className="bg-gradient-to-r from-blue-500 to-blue-700 bg-clip-text text-transparent">
                Values
              </span>
            </h2>

            <p className={`text-lg max-w-2xl mx-auto ${
              isDark ? 'text-gray-300' : 'text-gray-600'
            }`}>
              The principles that guide our work and define our culture
            </p>
          </div>


          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">

            {values.map((value, index) => (
              <div 
                key={index}
                className={`group p-6 rounded-2xl transition-all duration-300 text-center ${
                  isDark 
                    ? 'bg-white/5 border border-white/10 hover:border-blue-600/40 hover:bg-white/10' 
                    : 'bg-white border border-gray-200 hover:border-blue-500 hover:shadow-lg'
                }`}
              >

                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-blue-600 to-blue-800 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <span className="text-2xl">{value.icon}</span>
                </div>

                <h3 className={`text-xl font-semibold mb-2 ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>
                  {value.title}
                </h3>

                <p className={`text-sm ${
                  isDark ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {value.description}
                </p>

              </div>
            ))}

          </div>
        </div>
      </section>


      {/* Why Choose Us Section */}
      <section className={`py-16 sm:py-20 ${
        isDark ? 'bg-black' : 'bg-white'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className={`rounded-3xl p-8 md:p-12 text-center ${
            isDark 
              ? 'bg-gradient-to-r from-blue-700/10 to-blue-900/10 border border-white/10' 
              : 'bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200'
          }`}>

            <h2 className={`text-3xl sm:text-4xl font-bold mb-4 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              Why Choose{' '}
              <span className="bg-gradient-to-r from-blue-500 to-blue-700 bg-clip-text text-transparent">
                Us
              </span>
            </h2>

            <p className={`text-lg mb-12 max-w-2xl mx-auto ${
              isDark ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Partner with DOROTS for transformative business solutions
            </p>


            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">

              {whyChooseUs.map((item, index) => (
                <div key={index} className="text-center group">

                  <div className={`w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110 ${
                    isDark 
                      ? 'bg-blue-700/20 group-hover:bg-blue-700/30' 
                      : 'bg-blue-100 group-hover:bg-blue-200'
                  }`}>
                    <span className={`text-3xl ${
                      isDark ? 'text-blue-400' : 'text-blue-700'
                    }`}>
                      {item.icon}
                    </span>
                  </div>

                  <h3 className={`text-xl font-semibold mb-2 ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}>
                    {item.title}
                  </h3>

                  <p className={`text-sm ${
                    isDark ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {item.description}
                  </p>

                </div>
              ))}

            </div>
          </div>
        </div>
      </section>


      {/* CTA Section */}
      <section className={`py-16 ${
        isDark ? 'bg-gray-900/30' : 'bg-blue-50'
      }`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">

          <h2 className={`text-2xl sm:text-3xl font-bold mb-4 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            Ready to transform your business?
          </h2>

          <p className={`mb-8 ${
            isDark ? 'text-gray-300' : 'text-gray-600'
          }`}>
            Let's discuss how DOROTS can help you achieve your goals
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">

            <button className="px-8 py-3 rounded-xl bg-gradient-to-r from-blue-700 to-blue-900 text-white font-semibold hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl">
              Contact Us
            </button>

            <button className={`px-8 py-3 rounded-xl border font-semibold transition-all duration-300 ${
              isDark 
                ? 'border-white/20 text-white hover:bg-white/10' 
                : 'border-blue-300 text-blue-800 hover:bg-blue-100'
            }`}>
              Learn More
            </button>

          </div>
        </div>
      </section>

    </div>
  );
};

export default AboutPage;